import React from 'react';
import type { ToolResultBlock, ToolUseBlock } from '../../../store/chatStore';
import clsx from 'clsx';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { safeJsonParse } from '../../../../../utils/jsonRepairUtils';
import { Image } from '../../../../ui/Image';
import {
  PleriActionCell,
  type MoreOption,
  type RemediationConfig,
} from './Table/components/PleriActionCell';
import type { GenericItem } from './Table/types';
import {
  isRecord,
  getString,
  getFirstString,
  getTags,
  getFirstTagSource,
} from './shared/fieldUtils';
import { getResourceImageFallback } from './shared/imageFallback';
import type { TFunction } from 'i18next';

import { useTranslation } from 'react-i18next';
import { LinkOutIcon } from '@plerionhq/design-system/experimental';

interface Props {
  result: ToolResultBlock;
  toolUse: ToolUseBlock;
}

interface AssetCardProps {
  label: string;
  tone?: 'neutral' | 'info' | 'warning' | 'bad';
  size?: 'default' | 'compact';
  title?: string;
}

const toneClasses: Record<NonNullable<AssetCardProps['tone']>, string> = {
  neutral: 'bg-slate-100 dark:bg-slate-700 text-slate-900 dark:text-slate-100',
  info: 'bg-sky-100 dark:bg-sky-900 text-sky-900 dark:text-sky-200',
  warning: 'bg-amber-100 dark:bg-amber-900 text-amber-900 dark:text-amber-200',
  bad: 'bg-rose-100 dark:bg-rose-900 text-rose-900 dark:text-rose-200',
};

const Chip: React.FC<AssetCardProps> = ({
  label,
  tone = 'neutral',
  size = 'default',
  title,
}) => {
  return (
    <span
      className={clsx(
        'inline-flex max-w-full items-center rounded-md font-normal',
        size === 'compact' ? 'px-2 py-0.5 text-xs' : 'px-2 py-1 text-xs',
        toneClasses[tone],
      )}
      title={title}
    >
      <span className="truncate">{label}</span>
    </span>
  );
};

function parseAssets(content: unknown): Record<string, unknown>[] {
  const parsed =
    typeof content === 'string'
      ? safeJsonParse<unknown>(content, 'listAssets result')
      : content;

  if (Array.isArray(parsed)) {
    return parsed.filter(isRecord);
  }

  if (
    isRecord(parsed) &&
    typeof parsed.content === 'string' &&
    ['{', '['].includes(parsed.content.trim().charAt(0))
  ) {
    return parseAssets(parsed.content);
  }

  if (!isRecord(parsed) || !Array.isArray(parsed.data)) {
    return [];
  }

  return parsed.data.filter(isRecord);
}

function getExposureChip(
  t: TFunction<'pleri/chat'>,
  value: unknown,
): {
  tone: AssetCardProps['tone'];
  label: string;
} | null {
  if (typeof value === 'boolean') {
    return value ? { tone: 'bad', label: t('public') } : null;
  }

  const normalized = getString(value)?.trim().toLowerCase();
  if (!normalized) {
    return null;
  }

  if (['true', 'yes', 'public', '1'].includes(normalized)) {
    return { tone: 'bad', label: t('public') };
  }

  return null;
}

function formatRiskScore(value: unknown): string | undefined {
  const numeric = Number.parseFloat(getString(value) ?? '');
  if (Number.isNaN(numeric)) {
    return undefined;
  }
  return numeric.toFixed(1);
}

interface ContextChip {
  label: string;
  tooltip?: string;
  tone?: AssetCardProps['tone'];
}

function getCloudAccountLabel(
  t: TFunction<'pleri/chat'>,
  provider: string | undefined,
): string {
  const normalized = provider?.toLowerCase() ?? '';
  if (normalized.includes('azure')) {
    return t('common:subscription');
  }
  if (normalized.includes('gcp') || normalized.includes('google')) {
    return t('project');
  }
  return t('account');
}

function getContextChips(
  t: TFunction<'pleri/chat'>,
  asset: Record<string, unknown>,
): ContextChip[] {
  const assetMeta = isRecord(asset.meta) ? asset.meta : {};
  const provider = getFirstString(
    asset.provider,
    asset.provider_name,
    assetMeta.provider,
    assetMeta.provider_name,
  );
  const type = getFirstString(
    asset.type,
    asset.resourceType,
    asset.resource_type,
    assetMeta.type,
    assetMeta.resourceType,
    assetMeta.resource_type,
  );
  const region = getFirstString(
    asset.region,
    asset.location,
    assetMeta.region,
    assetMeta.location,
  );
  const resourceGroup = getFirstString(
    asset.resourceGroup,
    asset.resource_group,
    assetMeta.resourceGroup,
    assetMeta.resource_group,
  );
  const providerAccountId = getFirstString(
    asset.providerAccountId,
    asset.provider_account_id,
    asset.subscriptionId,
    asset.subscription_id,
    asset.accountId,
    asset.account_id,
    asset.projectId,
    asset.project_id,
    assetMeta.providerAccountId,
    assetMeta.provider_account_id,
    assetMeta.subscriptionId,
    assetMeta.subscription_id,
    assetMeta.accountId,
    assetMeta.account_id,
    assetMeta.projectId,
    assetMeta.project_id,
  );

  const chips: ContextChip[] = [];

  if (provider) {
    chips.push({ label: provider, tooltip: t('provider', { provider }) });
  }
  if (providerAccountId) {
    const accountLabel = getCloudAccountLabel(t, provider);
    chips.push({
      label: providerAccountId,
      tooltip: t('labelValue', {
        label: accountLabel,
        value: providerAccountId,
      }),
    });
  }
  if (type) {
    chips.push({ label: type, tooltip: t('type', { type }) });
  }
  if (region) {
    chips.push({ label: region, tooltip: t('regionValue', { region }) });
  }
  if (resourceGroup) {
    chips.push({
      label: resourceGroup,
      tooltip: t('resourceGroup', { resourceGroup }),
    });
  }

  return chips;
}

const assetMoreOptions = (t: TFunction<'pleri/chat'>): MoreOption[] => [
  {
    label: t('suggestFix'),
    message: 'Suggest fix for the findings of this asset.',
  },
  {
    label: t('createTicket'),
    message: 'Can you help me create a ticket for this?',
  },
];

const assetInvestigateConfig = (
  t: TFunction<'pleri/chat'>,
): RemediationConfig => ({
  tooltip: t('investigate'),
  message: 'Investigate this asset',
  icon: MagnifyingGlassIcon,
  hoverClasses:
    'hover:bg-orange-100 hover:text-orange-700 hover:ring-orange-300',
});

export const AssetsTableRenderer: React.FC<Props> = (props) => {
  const { t } = useTranslation('pleri/chat');
  const { result } = props;
  const [visibleTagCountByAsset, setVisibleTagCountByAsset] = React.useState<
    Record<string, number>
  >({});

  if (result.isError) {
    return (
      <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700 dark:bg-red-950">
        {typeof result.content === 'string'
          ? result.content
          : JSON.stringify(result.content, null, 2)}
      </div>
    );
  }

  const assets = parseAssets(result.content);
  if (assets.length === 0) {
    return (
      <div className="py-2 text-sm text-slate-600 dark:text-slate-400">
        {t('noAssetsFound')}
      </div>
    );
  }

  return (
    <div className="space-y-4 font-sans">
      {assets.map((asset, index) => {
        const id = getString(asset.id) ?? `asset-${index}`;
        const assetMeta = isRecord(asset.meta) ? asset.meta : {};
        const name = getFirstString(
          asset.name,
          asset.resourceName,
          asset.resource_name,
          asset.resourceId,
          asset.resource_id,
          assetMeta.name,
          assetMeta.resourceName,
          assetMeta.resource_name,
          assetMeta.resourceId,
          assetMeta.resource_id,
          asset.id,
        );
        const displayName = name ?? t('unnamedAsset');
        const resourceType =
          getFirstString(
            asset.type,
            asset.resourceType,
            asset.resource_type,
            assetMeta.type,
            assetMeta.resourceType,
            assetMeta.resource_type,
          ) ?? displayName;
        const provider = getFirstString(
          asset.provider,
          asset.provider_name,
          assetMeta.provider,
          assetMeta.provider_name,
        );
        const resourceUrl = getFirstString(
          asset.resourceURL,
          asset.resourceUrl,
          asset.resource_url,
          assetMeta.resourceURL,
          assetMeta.resourceUrl,
          assetMeta.resource_url,
        );
        const riskScore = formatRiskScore(
          asset.riskScore ??
            asset.risk_score ??
            assetMeta.riskScore ??
            assetMeta.risk_score,
        );
        const exposure = getExposureChip(
          t,
          asset.isPubliclyExposed ??
            asset.is_publicly_exposed ??
            assetMeta.isPubliclyExposed ??
            assetMeta.is_publicly_exposed,
        );
        const contextChips = getContextChips(t, asset);
        const tags = getTags(
          getFirstTagSource(
            'asset resourceTags',
            asset.resourceTags,
            asset.resource_tags,
            assetMeta.resourceTags,
            assetMeta.resource_tags,
            asset.tags,
            assetMeta.tags,
          ),
          'asset resourceTags',
        );
        const visibleTagCount = visibleTagCountByAsset[id] ?? 2;
        const visibleTags = tags.slice(0, visibleTagCount);
        const hasMoreTags = visibleTagCount < tags.length;
        const nextTagCount = Math.min(visibleTagCount + 4, tags.length);

        return (
          <article
            key={id}
            className="group/asset-card relative overflow-visible rounded-xl border border-slate-200 bg-white p-4 pr-20 shadow-sm transition-shadow hover:shadow-md dark:border-slate-700 dark:bg-gray-900"
          >
            <PleriActionCell
              row={{
                original: {
                  ...(asset as GenericItem),
                  assetId: getString(asset.id) ?? '',
                },
              }}
              showRemediationButton
              remediationConfig={assetInvestigateConfig(t)}
              moreOptions={assetMoreOptions(t)}
              position="right"
              hoverScope="asset-card"
              alwaysVisible
              size="compact"
              direction="vertical"
            />
            {/* ── Top row: icon + (context chips & asset name) ── */}
            <div className="flex items-start gap-3">
              <div className="h-12 w-12 flex-shrink-0 overflow-hidden rounded-md bg-slate-100 dark:bg-slate-700">
                <Image
                  type="resourceType"
                  name={resourceType}
                  fallback={getResourceImageFallback({
                    provider,
                    resourceType,
                  })}
                  className="h-full w-full object-contain p-0.5"
                />
              </div>

              <div className="min-w-0 flex-1">
                {contextChips.length > 0 && (
                  <div className="flex flex-wrap items-center gap-1.5">
                    {contextChips.map((chip, chipIndex) => (
                      <Chip
                        key={`${id}-context-${chip.label}-${chipIndex}`}
                        label={chip.label}
                        tone={chip.tone}
                        size="compact"
                        title={chip.tooltip}
                      />
                    ))}
                  </div>
                )}

                <div className="mt-1 min-w-0 pr-2 pl-0.5">
                  {resourceUrl ? (
                    <a
                      href={resourceUrl}
                      rel="noreferrer noopener"
                      target="_blank"
                      className="flex max-w-full items-center gap-1.5 text-sm font-normal text-slate-700 hover:text-slate-900 dark:text-slate-300 dark:hover:text-slate-100"
                      title={t('assetNameOpenInCloudConsole', { displayName })}
                    >
                      <span className="min-w-0 truncate">{displayName}</span>
                      <span className="inline-flex h-3.5 w-3.5 flex-shrink-0 items-center justify-center rounded bg-slate-200 text-slate-700 dark:bg-slate-600 dark:text-slate-300">
                        <LinkOutIcon href={resourceUrl} className="h-2 w-2" />
                      </span>
                    </a>
                  ) : (
                    <div
                      className="truncate text-sm font-normal text-slate-900 dark:text-slate-100"
                      title={t('assetNameValue', { displayName })}
                    >
                      {displayName}
                    </div>
                  )}
                </div>
              </div>
            </div>
            {/* ── Risk score & exposure chips ── */}
            {(riskScore || exposure) && (
              <div className="mt-3 flex flex-wrap items-center gap-2">
                {riskScore && (
                  <Chip
                    label={t('riskScore', { riskScore })}
                    tone="bad"
                    size="compact"
                  />
                )}
                {exposure && (
                  <Chip
                    label={t('exposure', { label: exposure.label })}
                    tone={exposure.tone}
                    size="compact"
                  />
                )}
              </div>
            )}
            {tags.length > 0 && (
              <div className="mt-3">
                <div className="text-xs tracking-[0.12em] text-slate-500 uppercase dark:text-slate-400">
                  {t('tags')}
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {visibleTags.map((tag, tagIndex) => (
                    <Chip
                      key={`${id}-tag-${tag.key}-${tag.value}-${tagIndex}`}
                      label={`${tag.key}: ${tag.value}`}
                    />
                  ))}
                  {hasMoreTags && (
                    <button
                      type="button"
                      onClick={() =>
                        setVisibleTagCountByAsset((prev) => ({
                          ...prev,
                          [id]: nextTagCount,
                        }))
                      }
                      className="inline-flex items-center rounded-md border border-slate-200 bg-white px-2 py-1 text-xs font-normal text-slate-700 hover:border-slate-300 hover:bg-slate-50 dark:border-slate-600 dark:bg-gray-900 dark:text-slate-300 dark:hover:border-slate-500 dark:hover:bg-gray-800"
                      title={t('showMoreTags', {
                        count: Math.min(4, tags.length - visibleTagCount),
                      })}
                    >
                      {t('showMoreLabel')}
                    </button>
                  )}
                </div>
              </div>
            )}
          </article>
        );
      })}
    </div>
  );
};
